<template>
  <teleport to="body">
    <transition name="zoom-fade">
      <div v-if="modelValue" class="zoom-overlay" @click.self="close">
        <v-btn
          icon="mdi-close"
          variant="flat"
          color="white"
          size="small"
          class="zoom-close-btn"
          @click="close"
        />
        <div class="zoom-controls">
          <v-btn
            icon="mdi-minus"
            variant="text"
            color="white"
            size="small"
            @click="zoomOut"
          />
          <span class="zoom-level">{{ Math.round(zoomScale * 100) }}%</span>
          <v-btn
            icon="mdi-plus"
            variant="text"
            color="white"
            size="small"
            @click="zoomIn"
          />
        </div>
        <div
          class="zoom-image-wrapper"
          ref="zoomWrapper"
          @wheel.prevent="onWheel"
          @mousedown="onDragStart"
          @touchstart.passive="onTouchStart"
        >
          <img
            ref="zoomImage"
            :src="zoomImageSrc"
            class="zoom-image"
            :style="zoomImageStyle"
            draggable="false"
            @load="onZoomImageLoad"
          />
        </div>
        <div class="zoom-nav" v-if="images.length > 1">
          <v-btn
            icon="mdi-chevron-right"
            variant="text"
            color="white"
            @click="zoomPrev"
          />
          <span class="zoom-counter"
            >{{ zoomIndex + 1 }} / {{ images.length }}</span
          >
          <v-btn
            icon="mdi-chevron-left"
            variant="text"
            color="white"
            @click="zoomNext"
          />
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
// Shared full-screen zoom/pan lightbox used both by ProductsGallery (product
// modal's multi-image carousel) and productItem (the magnify icon shown
// directly on a listing card, before the modal is even opened) — so both
// places always get the same zoom/pan/drag behavior and close button.
export default {
  name: "ImageZoomOverlay",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      required: true,
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      zoomIndex: this.initialIndex,
      zoomScale: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragStartPanX: 0,
      dragStartPanY: 0,
    };
  },
  watch: {
    modelValue(isOpen) {
      if (isOpen) {
        this.zoomIndex = this.initialIndex;
        this.resetZoomPosition();
        document.body.style.overflow = "hidden";
      } else {
        this.removeGlobalListeners();
        document.body.style.overflow = "";
      }
    },
  },
  beforeUnmount() {
    this.removeGlobalListeners();
    document.body.style.overflow = "";
  },
  computed: {
    zoomImageSrc() {
      const img = this.images[this.zoomIndex];
      return img?.replace("width=500", "width=1200");
    },
    zoomImageStyle() {
      return {
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomScale})`,
      };
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
    zoomIn() {
      this.zoomScale = Math.min(this.zoomScale + 0.5, 5);
    },
    zoomOut() {
      this.zoomScale = Math.max(this.zoomScale - 0.5, 0.5);
      if (this.zoomScale <= 1) {
        this.panX = 0;
        this.panY = 0;
      }
    },
    zoomPrev() {
      this.zoomIndex =
        (this.zoomIndex - 1 + this.images.length) % this.images.length;
      this.resetZoomPosition();
    },
    zoomNext() {
      this.zoomIndex = (this.zoomIndex + 1) % this.images.length;
      this.resetZoomPosition();
    },
    resetZoomPosition() {
      this.zoomScale = 1;
      this.panX = 0;
      this.panY = 0;
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.25 : 0.25;
      this.zoomScale = Math.min(Math.max(this.zoomScale + delta, 0.5), 5);
      if (this.zoomScale <= 1) {
        this.panX = 0;
        this.panY = 0;
      }
    },
    onDragStart(e) {
      if (this.zoomScale <= 1) return;
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartPanX = this.panX;
      this.dragStartPanY = this.panY;
      document.addEventListener("mousemove", this.onDragMove);
      document.addEventListener("mouseup", this.onDragEnd);
    },
    onDragMove(e) {
      if (!this.isDragging) return;
      this.panX = this.dragStartPanX + (e.clientX - this.dragStartX);
      this.panY = this.dragStartPanY + (e.clientY - this.dragStartY);
    },
    onDragEnd() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("mouseup", this.onDragEnd);
    },
    onTouchStart(e) {
      if (this.zoomScale <= 1 || e.touches.length !== 1) return;
      this.isDragging = true;
      this.dragStartX = e.touches[0].clientX;
      this.dragStartY = e.touches[0].clientY;
      this.dragStartPanX = this.panX;
      this.dragStartPanY = this.panY;
      document.addEventListener("touchmove", this.onTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", this.onTouchEnd);
    },
    onTouchMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      this.panX = this.dragStartPanX + (e.touches[0].clientX - this.dragStartX);
      this.panY = this.dragStartPanY + (e.touches[0].clientY - this.dragStartY);
    },
    onTouchEnd() {
      this.isDragging = false;
      document.removeEventListener("touchmove", this.onTouchMove);
      document.removeEventListener("touchend", this.onTouchEnd);
    },
    onZoomImageLoad() {
      this.resetZoomPosition();
    },
    removeGlobalListeners() {
      document.removeEventListener("mousemove", this.onDragMove);
      document.removeEventListener("mouseup", this.onDragEnd);
      document.removeEventListener("touchmove", this.onTouchMove);
      document.removeEventListener("touchend", this.onTouchEnd);
    },
  },
};
</script>

<style lang="scss" scoped>
.zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.92);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.zoom-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 11;
  background: rgba(255, 255, 255, 0.815) !important;
}

.zoom-controls {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 24px;
  padding: 4px 12px;
  z-index: 10;
}

.zoom-level {
  color: #fff;
  font-size: 14px;
  min-width: 45px;
  text-align: center;
}

.zoom-image-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
}

.zoom-image {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  transition: transform 0.15s ease;
  user-select: none;
  -webkit-user-drag: none;
}

.zoom-nav {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 10;
}

.zoom-counter {
  color: #fff;
  font-size: 14px;
}

.zoom-fade-enter-active,
.zoom-fade-leave-active {
  transition: opacity 0.25s ease;
}
.zoom-fade-enter-from,
.zoom-fade-leave-to {
  opacity: 0;
}
</style>
